import { useCallback, useEffect, useRef } from "react";

import { useDebounceApi } from "../useDebounceApi";
import { useMarkChatRoomAsRead } from "./useMarkChatRoomAsRead";

const MARK_READ_DEBOUNCE_TIMEOUT = 2000;

// Marks a chat room as read (debounced) while the tab is visible. If the tab
// is hidden when called, the call is deferred and flushed as soon as the tab
// becomes visible again, so a message that arrives while backgrounded still
// gets marked read once the user actually returns to it.
export const useMarkChatRoomAsReadWhenVisible = () => {
  const { markChatRoomAsRead } = useMarkChatRoomAsRead();
  const { debouncedFunction } = useDebounceApi();
  const pendingUuidRef = useRef<string | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const uuid = pendingUuidRef.current;
      if (document.visibilityState === "visible" && uuid) {
        pendingUuidRef.current = null;
        void markChatRoomAsRead({ uuid });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [markChatRoomAsRead]);

  // Stable so ChatRoomsContext's value (read by every message row) doesn't
  // change when the mark-as-read mutation re-renders the provider.
  return useCallback(
    (uuid: string) => {
      if (document.visibilityState === "visible") {
        debouncedFunction(markChatRoomAsRead, MARK_READ_DEBOUNCE_TIMEOUT, {
          uuid,
        });
      } else {
        pendingUuidRef.current = uuid;
      }
    },
    [debouncedFunction, markChatRoomAsRead],
  );
};
